import React, { useState, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp as faThumbsUpSolid, faThumbsDown as faThumbsDownSolid } from '@fortawesome/pro-solid-svg-icons';
import { faThumbsUp as faThumbsUpLight, faThumbsDown as faThumbsDownLight } from '@fortawesome/pro-light-svg-icons';
import { VoteType, TenderVote } from '@/actions/dtos/tender/vote';
import { createTenderVote, deleteTenderVote } from '@/api/requests/tenderVote';
import { useAccount } from '@/hooks/useAccount';
import { useEnumTranslations } from '@/hooks/useEnumTranslations';
import useClickOutside from '@/hooks/useClickOutside';
import Button from '@/components/common/Button';
import { faPaperPlaneTop } from '@fortawesome/pro-duotone-svg-icons';
import { useVoteStats } from '@/utils/voteUtils';
import { useAuthenticated } from '@/hooks/useAuthenticated';

interface VoteButtonsProps {
  tenderId: string;
  searchExecutionId?: string;
  votes?: TenderVote[];
  onVoteChange?: () => void;
  variant?: 'default' | 'gray';
  dropdownPosition?: 'left' | 'right';
}

enum VoteReason {
  WRONG_SERVICE_BRANCH = 'WrongServiceBranch',
  GEOGRAPHICALLY_UNSUITABLE = 'GeographicallyUnsuitable',
  NOT_ECONOMICAL = 'NotEconomical',
  INSUFFICIENT_TIME = 'InsufficientTime',
  REQUIREMENTS_NOT_FULFILLABLE = 'RequirementsNotFulfillable',
  UNCLEAR_INFORMATION = 'UnclearInformation',
}

const VoteButtons: React.FC<VoteButtonsProps> = ({
  tenderId,
  searchExecutionId,
  votes = [],
  onVoteChange,
  variant = 'default',
  dropdownPosition = 'right',
}) => {
  const { getEnumOptions, t } = useEnumTranslations();
  const { isAuthenticated } = useAuthenticated();
  const { member } = useAccount();
  const { positiveCount, negativeCount, hasUserPositiveVote, hasUserNegativeVote, userVote, getVoteDisplay } =
    useVoteStats(votes, member?.id);

  const reasonOptions = getEnumOptions('Vote_Reason', VoteReason);

  const [isLoading, setIsLoading] = useState(false);
  const [showReasonDropdown, setShowReasonDropdown] = useState(false);
  const [customReason, setCustomReason] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const canVote = Boolean(searchExecutionId) && isAuthenticated;

  const resetDropdownState = useCallback(() => {
    setShowReasonDropdown(false);
    setCustomReason('');
  }, []);

  useClickOutside(dropdownRef, resetDropdownState);

  const handleVote = useCallback(
    async (voteType: VoteType, reason?: string) => {
      if (isLoading || !searchExecutionId) return;

      setIsLoading(true);
      try {
        const isCurrentVoteType = userVote?.type === voteType;

        if (isCurrentVoteType && !reason) {
          await deleteTenderVote(userVote.id);
        } else {
          if (userVote) {
            await deleteTenderVote(userVote.id);
          }
          await createTenderVote(tenderId, searchExecutionId, voteType, reason);
        }

        resetDropdownState();
        onVoteChange?.();
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, searchExecutionId, userVote, tenderId, resetDropdownState, onVoteChange],
  );

  const handlePositiveVote = useCallback(() => {
    handleVote(VoteType.POSITIVE);
  }, [handleVote]);

  const handleNegativeVoteClick = useCallback(() => {
    if (isLoading || !canVote) return;

    if (hasUserNegativeVote) {
      handleVote(VoteType.NEGATIVE);
    } else {
      setShowReasonDropdown(true);
    }
  }, [isLoading, canVote, hasUserNegativeVote, handleVote]);

  const handleReasonSelect = useCallback(
    (event: React.MouseEvent, reasonLabel: string) => {
      event.preventDefault();
      event.stopPropagation();
      handleVote(VoteType.NEGATIVE, reasonLabel);
    },
    [handleVote],
  );

  const handleCustomReasonSubmit = useCallback(
    (event?: React.MouseEvent) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      const trimmedReason = customReason.trim();
      if (trimmedReason) {
        handleVote(VoteType.NEGATIVE, trimmedReason);
      }
    },
    [customReason, handleVote],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && customReason.trim()) {
        e.preventDefault();
        e.stopPropagation();
        handleCustomReasonSubmit();
      }
    },
    [customReason, handleCustomReasonSubmit],
  );

  const handleContainerClick = useCallback(
    (e: React.MouseEvent) => {
      if (!canVote) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [canVote],
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="relative flex items-center gap-2" onClick={handleContainerClick}>
      <div className={!canVote ? 'cursor-not-allowed' : ''}>
        <Button
          icon={hasUserPositiveVote ? faThumbsUpSolid : faThumbsUpLight}
          label={getVoteDisplay(positiveCount, hasUserPositiveVote)}
          onClick={handlePositiveVote}
          disabled={isLoading || !canVote}
          isLoading={isLoading && hasUserPositiveVote}
          variant="primary"
          titleClassName="font-light text-sm"
          buttonClassName={
            hasUserPositiveVote
              ? 'border-none w-auto h-7 btn-xs hover:no-underline bg-success/30 text-primary hover:bg-success/50 hover:text-primary disabled:bg-success/30 disabled:text-primary disabled:opacity-100'
              : `border-none w-auto h-7 btn-xs hover:no-underline disabled:opacity-100 disabled:text-primary ${variant === 'gray' ? 'bg-base-200 disabled:bg-base-200' : 'bg-base-300 disabled:bg-base-300'}`
          }
          iconClassName={hasUserPositiveVote ? 'text-primary' : ''}
        />
      </div>

      <div className="relative" ref={dropdownRef}>
        <div className={!canVote ? 'cursor-not-allowed' : ''}>
          <Button
            icon={hasUserNegativeVote ? faThumbsDownSolid : faThumbsDownLight}
            label={getVoteDisplay(negativeCount, hasUserNegativeVote)}
            onClick={handleNegativeVoteClick}
            disabled={isLoading || !canVote}
            isLoading={isLoading && hasUserNegativeVote}
            variant="primary"
            titleClassName="font-light text-sm"
            buttonClassName={
              hasUserNegativeVote
                ? 'border-none w-auto h-7 btn-xs hover:no-underline bg-error/30 text-primary hover:bg-error/50 hover:text-primary disabled:bg-error/30 disabled:text-primary disabled:opacity-100'
                : `border-none w-auto h-7 btn-xs hover:no-underline disabled:opacity-100 disabled:text-primary ${variant === 'gray' ? 'bg-base-200 disabled:bg-base-200' : 'bg-base-300 disabled:bg-base-300'}`
            }
            iconClassName={hasUserNegativeVote ? 'text-primary' : ''}
          />
        </div>

        {showReasonDropdown && (
          <div
            className={`absolute ${dropdownPosition === 'left' ? 'left-0' : 'right-0'} top-full z-50 mt-1 w-48 rounded-lg bg-base-100 shadow-lg`}
          >
            <div className="p-1">
              {reasonOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={(e) => handleReasonSelect(e, option.label)}
                  className="w-full cursor-pointer rounded px-3 py-2 text-left text-sm hover:bg-primary hover:text-primary-content"
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className="border-t border-base-300 p-2">
              <div className="relative">
                <textarea
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onFocus={(e) => {
                    e.stopPropagation();
                  }}
                  placeholder={t('Placeholder_Vote_Reason')}
                  className="w-full resize-none rounded bg-base-200 p-2 pr-8 text-sm"
                />
                <button
                  onClick={handleCustomReasonSubmit}
                  disabled={!customReason.trim()}
                  className={`absolute right-1 p-1 ${
                    customReason.trim() ? 'hover:text-primary-focus' : 'cursor-not-allowed opacity-30'
                  }`}
                  title={t('Button_Send')}
                >
                  <FontAwesomeIcon icon={faPaperPlaneTop} className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoteButtons;
